// @ts-nocheck
export const CREATOR_COLLAB_PROTOCOL_VERSION = 1 as const

export const CREATOR_COLLAB_EVENT_TYPES = [
  'tableau.import_session.upsert',
  'tableau.active_tab.set',
  'tableau.workbook_ir.patch',
  'tableau.workbook_ir.snapshot',
  'tableau.baseline.updated',
  'tableau.spreadsheet_sheets.snapshot',
  'tableau.dashboard_drafts.snapshot',
  'tableau.collab_link.snapshot',
] as const

export type CreatorCollabEventType = typeof CREATOR_COLLAB_EVENT_TYPES[number]

export type CreatorCollabEventDraft = {
  event_type: CreatorCollabEventType | string
  payload: any
  client_event_id?: string | null
}

export type CreatorCollabCommittedEvent = {
  seq: number
  event_type: CreatorCollabEventType | string
  payload: any
  actor_user_id: string
  created_at: string
  client_id?: string | null
  client_event_id?: string | null
}

export type CreatorCollabAck = {
  client_event_id?: string | null
  seq: number
}

export type CreatorCollabCheckpoint = {
  version: 1
  workbook_id: string
  head_seq: number
  updated_at_ms: number
}

export type CreatorCollabCausalityV1 = {
  authoring_head: number
}

export type CreatorCollabCommandEnvelopeV1 = {
  version: 1
  protocol_version: typeof CREATOR_COLLAB_PROTOCOL_VERSION
  command_id: string
  group_id: string
  workbook_id: string
  domain: 'authoring'
  client_id: string
  causality: CreatorCollabCausalityV1
  submitted_at_ms: number
  events: CreatorCollabEventDraft[]
}

export type CreatorCollabCommittedMessage = {
  type: 'tableau_events_committed'
  head_seq: number
  events: CreatorCollabCommittedEvent[]
  acks: CreatorCollabAck[]
}

export type CreatorCollabSubscribeMessage = {
  type: 'subscribe_workbook'
  protocol_version: typeof CREATOR_COLLAB_PROTOCOL_VERSION
  workbook_id: string
  client_id: string
}

export type CreatorCollabResumeMessage = {
  type: 'resume_workbook'
  protocol_version: typeof CREATOR_COLLAB_PROTOCOL_VERSION
  workbook_id: string
  client_id: string
  checkpoint: CreatorCollabCheckpoint | null
}

export type CreatorCollabClientCheckpointMessage = {
  type: 'client_checkpoint'
  protocol_version: typeof CREATOR_COLLAB_PROTOCOL_VERSION
  checkpoint: CreatorCollabCheckpoint
  client_id: string
}

export type CreatorCollabSubscriptionReadyMessage = {
  type: 'subscription_ready'
  workbook_id: string
  head_seq: number
}

export type CreatorCollabResumeAckMessage = {
  type: 'resume_ack'
  workbook_id: string
  requested_head_seq: number
  head_seq: number
}

export type CreatorCollabCheckpointHeartbeatMessage = {
  type: 'checkpoint_heartbeat'
  workbook_id: string
  head_seq: number
}

export type CreatorCollabBackpressureNoticeMessage = {
  type: 'subscription_backpressure_notice'
  workbook_id: string
  head_seq: number
  message: string
}

export type CreatorCollabRebootstrapRequiredMessage = {
  type: 'subscription_rebootstrap_required'
  workbook_id: string
  head_seq: number
  reason: string
}

export type CreatorCollabServerMessage =
  | CreatorCollabCommittedMessage
  | CreatorCollabSubscriptionReadyMessage
  | CreatorCollabResumeAckMessage
  | CreatorCollabCheckpointHeartbeatMessage
  | CreatorCollabBackpressureNoticeMessage
  | CreatorCollabRebootstrapRequiredMessage

const normalizeSeq = (value: any): number => {
  const next = Number(value)
  if (!Number.isFinite(next) || next < 0) return 0
  return Math.floor(next)
}

export const newCreatorCommandId = (): string =>
  `cmd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`

export const normalizeCreatorCollabEventDraft = (
  event: CreatorCollabEventDraft,
  fallbackClientEventId: string
): CreatorCollabEventDraft | null => {
  const eventType = String(event?.event_type || '').trim()
  if (!eventType) return null
  return {
    event_type: eventType,
    payload: event?.payload ?? null,
    client_event_id: String(event?.client_event_id || '').trim() || fallbackClientEventId,
  }
}

export const makeCreatorCollabCommandEnvelope = (input: {
  workbook_id: string
  client_id: string
  base_seq: number
  events: CreatorCollabEventDraft[]
  command_id?: string
  group_id?: string
  submitted_at_ms?: number
}): CreatorCollabCommandEnvelopeV1 | null => {
  const workbookId = String(input?.workbook_id || '').trim()
  const clientId = String(input?.client_id || '').trim()
  if (!workbookId || !clientId) return null
  const commandId = String(input?.command_id || '').trim() || newCreatorCommandId()
  const groupId = String(input?.group_id || '').trim() || commandId
  const submittedAtMs = Math.max(0, Math.floor(Number(input?.submitted_at_ms) || Date.now()))
  const normalizedEvents = (Array.isArray(input?.events) ? input.events : [])
    .map((event, index) => normalizeCreatorCollabEventDraft(event, `${commandId}:${index}`))
    .filter((event): event is CreatorCollabEventDraft => Boolean(event))
  if (!normalizedEvents.length) return null
  return {
    version: 1,
    protocol_version: CREATOR_COLLAB_PROTOCOL_VERSION,
    command_id: commandId,
    group_id: groupId,
    workbook_id: workbookId,
    domain: 'authoring',
    client_id: clientId,
    causality: {
      authoring_head: normalizeSeq(input?.base_seq),
    },
    submitted_at_ms: submittedAtMs,
    events: normalizedEvents,
  }
}

export const makeCreatorCollabCheckpoint = (
  workbookIdRaw: string,
  headSeq: number,
  nowMs = Date.now()
): CreatorCollabCheckpoint => ({
  version: 1,
  workbook_id: String(workbookIdRaw || '').trim(),
  head_seq: normalizeSeq(headSeq),
  updated_at_ms: Math.max(0, Math.floor(Number(nowMs) || 0)),
})

export const makeCreatorCollabSubscribeMessage = (
  workbookIdRaw: string,
  clientIdRaw: string
): CreatorCollabSubscribeMessage => ({
  type: 'subscribe_workbook',
  protocol_version: CREATOR_COLLAB_PROTOCOL_VERSION,
  workbook_id: String(workbookIdRaw || '').trim(),
  client_id: String(clientIdRaw || '').trim(),
})

export const makeCreatorCollabResumeMessage = (
  workbookIdRaw: string,
  clientIdRaw: string,
  checkpoint: CreatorCollabCheckpoint | null
): CreatorCollabResumeMessage => ({
  type: 'resume_workbook',
  protocol_version: CREATOR_COLLAB_PROTOCOL_VERSION,
  workbook_id: String(workbookIdRaw || '').trim(),
  client_id: String(clientIdRaw || '').trim(),
  checkpoint: checkpoint ?? null,
})

export const makeCreatorCollabClientCheckpointMessage = (
  checkpoint: CreatorCollabCheckpoint,
  clientIdRaw: string
): CreatorCollabClientCheckpointMessage => ({
  type: 'client_checkpoint',
  protocol_version: CREATOR_COLLAB_PROTOCOL_VERSION,
  checkpoint,
  client_id: String(clientIdRaw || '').trim(),
})

export const normalizeCreatorCollabCommittedMessage = (
  input: any
): CreatorCollabCommittedMessage | null => {
  if (!input || input.type !== 'tableau_events_committed') return null
  const events = Array.isArray(input.events)
    ? input.events.map((event: any) => ({
      seq: normalizeSeq(event?.seq),
      event_type: String(event?.event_type || '').trim(),
      payload: event?.payload ?? null,
      actor_user_id: String(event?.actor_user_id || '').trim(),
      created_at: String(event?.created_at || ''),
      client_id: String(event?.client_id || '').trim() || null,
      client_event_id: String(event?.client_event_id || '').trim() || null,
    }))
      .filter((event) => Boolean(event.event_type))
    : []
  const acks = Array.isArray(input.acks)
    ? input.acks.map((ack: any) => ({
      client_event_id: String(ack?.client_event_id || '').trim() || null,
      seq: normalizeSeq(ack?.seq),
    }))
    : []
  return {
    type: 'tableau_events_committed',
    head_seq: normalizeSeq(input.head_seq),
    events,
    acks,
  }
}

export const normalizeCreatorCollabServerMessage = (
  input: any
): CreatorCollabServerMessage | null => {
  const committed = normalizeCreatorCollabCommittedMessage(input)
  if (committed) return committed
  const messageType = String(input?.type || '').trim()
  if (messageType === 'subscription_ready') {
    return {
      type: 'subscription_ready',
      workbook_id: String(input?.workbook_id || '').trim(),
      head_seq: normalizeSeq(input?.head_seq),
    }
  }
  if (messageType === 'resume_ack') {
    return {
      type: 'resume_ack',
      workbook_id: String(input?.workbook_id || '').trim(),
      requested_head_seq: normalizeSeq(input?.requested_head_seq),
      head_seq: normalizeSeq(input?.head_seq),
    }
  }
  if (messageType === 'checkpoint_heartbeat') {
    return {
      type: 'checkpoint_heartbeat',
      workbook_id: String(input?.workbook_id || '').trim(),
      head_seq: normalizeSeq(input?.head_seq),
    }
  }
  if (messageType === 'subscription_backpressure_notice') {
    return {
      type: 'subscription_backpressure_notice',
      workbook_id: String(input?.workbook_id || '').trim(),
      head_seq: normalizeSeq(input?.head_seq),
      message: String(input?.message || '').trim(),
    }
  }
  if (messageType === 'subscription_rebootstrap_required') {
    return {
      type: 'subscription_rebootstrap_required',
      workbook_id: String(input?.workbook_id || '').trim(),
      head_seq: normalizeSeq(input?.head_seq),
      reason: String(input?.reason || '').trim(),
    }
  }
  return null
}
